// src/components/upload/ProgressTracker.js
import React, { useEffect, useState } from 'react';
import { Box, Typography, LinearProgress, CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import DoneAllIcon from '@mui/icons-material/DoneAll';

const ProgressTracker = ({ progress, message, currentStep, completed = false }) => {
  // Define the analysis steps in order
  const analysisSteps = [
    { id: 'ocr', label: 'OCR Processing' },
    { id: 'completeness', label: 'Completeness Analysis' },
    { id: 'elevator_pitch', label: 'Elevator Pitch' },
    { id: 'team', label: 'Team Analysis' },
    { id: 'product', label: 'Product Analysis' },
    { id: 'market', label: 'Market Analysis' },
    { id: 'finance', label: 'Financial Analysis' },
    { id: 'strategic_finance', label: 'Strategic Financial Analysis' },
    { id: 'summary', label: 'Creating Summary' }
  ];

  // Use local state to track completed steps for more reliability
  const [completedSteps, setCompletedSteps] = useState([]);
  const [activeStep, setActiveStep] = useState(currentStep);
  const [isCompleted, setIsCompleted] = useState(false);

  // Get index of current step
  const currentStepIndex = analysisSteps.findIndex(step => step.id === activeStep);
  
  // Nur als 'completed' betrachten, wenn Fortschritt WIRKLICH 100% ist
  useEffect(() => {
    // Setze isCompleted nur auf true, wenn der Fortschritt 100% erreicht hat
    // UND die externe completed-Flag true ist
    setIsCompleted(progress >= 100 && completed);
  }, [progress, completed]);
  
  // Update active step and track completed steps
  useEffect(() => {
    if (!currentStep) return;
    
    // Find indices
    const newStepIndex = analysisSteps.findIndex(step => step.id === currentStep);
    const currentActiveIndex = analysisSteps.findIndex(step => step.id === activeStep);
    
    console.log(`Step Update: currentStep=${currentStep}, activeStep=${activeStep}, progress=${progress}`);
    
    // Only move forward in the steps sequence
    if (newStepIndex > currentActiveIndex) {
      // Mark all previous steps as completed
      const newCompletedSteps = [...completedSteps];
      for (let i = 0; i <= currentActiveIndex; i++) {
        if (!newCompletedSteps.includes(analysisSteps[i].id)) {
          newCompletedSteps.push(analysisSteps[i].id);
        }
      }
      setCompletedSteps(newCompletedSteps);
      setActiveStep(currentStep);
    }
    
    // Special case: If progress is very high but not 100%, don't automatically mark all steps as complete
    if (progress > 85 && progress < 99 && currentStepIndex < 6) {
      console.log("High progress detected, marking steps up to current point as complete");
      
      // Finde den passenden Schritt basierend auf dem Fortschritt
      let targetStep;
      if (progress > 90) {
        targetStep = 'summary';
      } else if (progress > 85) {
        targetStep = 'strategic_finance';
      } else if (progress > 75) {
        targetStep = 'finance';
      }
      
      // Markiere Schritte nur bis zum aktuellen Schritt
      if (targetStep) {
        const targetIndex = analysisSteps.findIndex(step => step.id === targetStep);
        const stepsToComplete = analysisSteps.slice(0, targetIndex).map(step => step.id);
        setCompletedSteps(stepsToComplete);
        setActiveStep(targetStep);
      }
    }
  }, [currentStep, progress, activeStep, completedSteps]);

  return (
    <Box sx={{ width: '100%', mb: 4 }}>
      {/* Progress Bar with Percentage */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Box sx={{ width: '100%', mr: 1 }}>
          <LinearProgress 
            variant="determinate" 
            value={progress} 
            sx={{ height: 10, borderRadius: 5 }}
          />
        </Box>
        <Box sx={{ minWidth: 35, textAlign: 'right' }}> {/* Ensure space for percentage */}
          <Typography variant="body2" fontWeight="bold">{`${Math.round(progress)}%`}</Typography>
        </Box>
      </Box>

      {/* OCR Page Progress (conditional) */}
      {(() => {
        if (currentStep === 'ocr' && message) {
          const match = message.match(/Seite (\d+)\/(\d+) verarbeitet/); // Match German message "Seite X/Y verarbeitet"
          if (match) {
            return (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5, mb: 2 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {`OCR Progress: Page ${match[1]} / ${match[2]}`}
                </Typography>
              </Box>
            );
          }
        }
        return null; // Return null if conditions are not met
      })()}

      {/* Current Task Message */}
      {isCompleted ? (
        <Box
          sx={{
            backgroundColor: '#4caf50',
            color: 'white',
            p: 1.5,
            borderRadius: 1,
            textAlign: 'center',
            mb: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <DoneAllIcon sx={{ mr: 1 }} />
          <Typography variant="body1" fontWeight="medium">
            Analysis completed
          </Typography>
        </Box>
      ) : (
        <Typography 
          variant="body2"
          sx={{ 
            backgroundColor: 'primary.light', 
            color: 'primary.dark',
            p: 1, 
            borderRadius: 1,
            textAlign: 'center',
            mb: 3,
            fontWeight: 'medium'
          }}
        >
          {message || 'Initializing...'}
        </Typography>
      )}

      {/* Step Indicators */}
      <Box sx={{ mt: 2 }}>
        {analysisSteps.map((step, index) => {
          // Determine step status - but be careful about the conditions
          const isStepCompleted = completedSteps.includes(step.id) || 
            (isCompleted && index <= analysisSteps.length - 1);
          
          const isStepActive = step.id === activeStep && !isCompleted;
          
          return (
            <Box 
              key={step.id} 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 1.5,
                opacity: isStepActive || isStepCompleted ? 1 : 0.6,
                backgroundColor: isStepActive ? '#e3f2fd' : 
                                isCompleted && step.id === 'summary' ? '#e8f5e9' : 
                                'transparent',
                borderRadius: 1,
                py: 0.75,
                px: 1.5,
                border: isStepActive ? '1px solid #2196f3' : 
                        isCompleted && step.id === 'summary' ? '1px solid #4caf50' : 
                        'none'
              }}
            >
              {/* Icon based on status */}
              {isStepCompleted && (
                <CheckCircleIcon 
                  color={isCompleted && step.id === 'summary' ? 'success' : 'success'} 
                  sx={{ mr: 1.5 }} 
                />
              )}
              {isStepActive && (
                <CircularProgress size={20} sx={{ mr: 1.5 }} />
              )}
              {!isStepCompleted && !isStepActive && (
                <RadioButtonUncheckedIcon color="disabled" sx={{ mr: 1.5 }} />
              )}
              
              {/* Step label */}
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: isStepActive || (isCompleted && step.id === 'summary') ? 'bold' : 'normal',
                  color: isStepActive ? '#0d47a1' : 
                          isCompleted && step.id === 'summary' ? '#1b5e20' : 
                          isStepCompleted ? 'text.primary' : 
                          'text.secondary'
                }}
              >
                {step.label}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default ProgressTracker;
